// =============================================================================
// Hosted-agent eval suites (#3028 — HERMES)
//
// Two surfaces behind `require_admin_capability("hosted_agents")`:
//   * the EXAM  — /eval-cases CRUD (admin-owned; no agent path reaches it)
//   * the MEASUREMENT — POST /eval-runs starts a background suite (**202**),
//     GET /eval-runs/<id> is polled until the run reaches a terminal status.
//
// The contract is THREE-VALUED, not two. An expectation is `passed`, `failed`
// or `indeterminate`, and every non-pass carries a stable machine
// `detail_code`. "The requirement was not met" and "it could not be measured"
// are different statements: the UI must render `detail_code` through its own
// i18n keys and must NEVER parse `detail`, which is English server prose kept
// for logs.
//
// Backend: routes/admin/hosted_agent_evals.py,
// services/hosted_agents/evals.py, services/hosted_agents/eval_runner.py.
// =============================================================================

import axios from "axios"

import { api } from "@/lib/api-client"

// -----------------------------------------------------------------------------
// The exam — cases and expectations
// -----------------------------------------------------------------------------

/** `ExpectationKind` (evals.py:76). */
export type HostedAgentEvalExpectationKind =
  | "run_status"
  | "answer_contains"
  | "answer_not_contains"
  | "tool_called"
  | "tool_not_called"
  | "tool_denied"
  | "artifact_produced"
  | "cited_source"
  | "max_cost_sek"
  | "max_tokens"
  | "max_duration_ms"

export const HOSTED_AGENT_EVAL_EXPECTATION_KINDS: readonly HostedAgentEvalExpectationKind[] = [
  "run_status",
  "answer_contains",
  "answer_not_contains",
  "tool_called",
  "tool_not_called",
  "tool_denied",
  "artifact_produced",
  "cited_source",
  "max_cost_sek",
  "max_tokens",
  "max_duration_ms",
] as const

/** The gateway's denial vocabulary — the only accepted `tool_denied.code`. */
export type HostedAgentEvalToolDenialCode =
  | "agent_tool_denied"
  | "agent_tool_action_denied"
  | "agent_tool_approval_required"
  | "agent_delegated_identity_unavailable"

export const HOSTED_AGENT_EVAL_TOOL_DENIAL_CODES: readonly HostedAgentEvalToolDenialCode[] = [
  "agent_tool_denied",
  "agent_tool_action_denied",
  "agent_tool_approval_required",
  "agent_delegated_identity_unavailable",
] as const

/** Kinds whose `expected` field is required (`run_status`). */
export const HOSTED_AGENT_EVAL_STATUS_KINDS: readonly HostedAgentEvalExpectationKind[] = [
  "run_status",
] as const
/** Kinds that require a non-empty `value`. */
export const HOSTED_AGENT_EVAL_TEXT_KINDS: readonly HostedAgentEvalExpectationKind[] = [
  "answer_contains",
  "answer_not_contains",
] as const
/** Kinds that require `tool`. */
export const HOSTED_AGENT_EVAL_TOOL_KINDS: readonly HostedAgentEvalExpectationKind[] = [
  "tool_called",
  "tool_not_called",
  "tool_denied",
] as const
/** Kinds that use `min_count`. */
export const HOSTED_AGENT_EVAL_COUNT_KINDS: readonly HostedAgentEvalExpectationKind[] = [
  "artifact_produced",
  "cited_source",
] as const
/** Kinds that require `limit`. */
export const HOSTED_AGENT_EVAL_LIMIT_KINDS: readonly HostedAgentEvalExpectationKind[] = [
  "max_cost_sek",
  "max_tokens",
  "max_duration_ms",
] as const

// Caps mirrored from the backend so the editor can refuse before the round trip.
export const MAX_EVAL_CASES_PER_AGENT = 25
export const MAX_EVAL_EXPECTATIONS_PER_CASE = 20
export const MAX_EVAL_CASE_NAME_LENGTH = 120
export const MAX_EVAL_CASE_PROMPT_LENGTH = 4000
export const MAX_EVAL_EXPECTATION_VALUE_LENGTH = 500
export const MIN_EVAL_CASE_TIMEOUT_SECONDS = 1
export const DEFAULT_EVAL_CASE_TIMEOUT_SECONDS = 120
export const MAX_EVAL_CASE_TIMEOUT_SECONDS = 300
export const MAX_EVAL_RUN_HISTORY = 50

/**
 * One expectation. The backend model is `extra="forbid"`: send only the fields
 * that belong to the chosen `kind` (see `_check_kind_fields`, evals.py:160),
 * never a stale field from a kind the admin switched away from.
 */
export type HostedAgentEvalExpectation = {
  kind: HostedAgentEvalExpectationKind
  /** run_status (required for it). */
  expected?: "completed" | "failed" | null
  /** answer_contains / answer_not_contains (required, <= 500 chars). */
  value?: string | null
  case_sensitive?: boolean
  /** tool_called / tool_not_called / tool_denied (required, <= 100 chars). */
  tool?: string | null
  action?: string | null
  /** tool_called / tool_not_called: case-insensitive substring of the ledger's argument summary (<= 200 chars). */
  detail_contains?: string | null
  /** tool_denied only, and only a TOOL_DENIAL_CODES member. */
  code?: HostedAgentEvalToolDenialCode | null
  /** artifact_produced / cited_source, 1..100 (server default 1). */
  min_count?: number
  /** max_cost_sek / max_tokens / max_duration_ms (required, >= 0). */
  limit?: number | null
}

/** A stored eval case (`EvalCase.to_public_dict()`). */
export type HostedAgentEvalCase = {
  id: string
  tenant_id: string
  agent_id: string
  name: string
  prompt: string
  expectations: HostedAgentEvalExpectation[]
  enabled: boolean
  timeout_seconds: number
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

/**
 * The only fields a client may set (`_CLIENT_FIELDS`, evals.py:582).
 * Everything else on the document is server-managed and silently dropped.
 */
export type HostedAgentEvalCaseInput = {
  name: string
  prompt: string
  expectations: HostedAgentEvalExpectation[]
  enabled?: boolean
  timeout_seconds?: number
}

// -----------------------------------------------------------------------------
// The measurement — three-valued results
// -----------------------------------------------------------------------------

/** Expectation outcome. `indeterminate` never counts as a pass, anywhere. */
export type HostedAgentEvalOutcome = "passed" | "failed" | "indeterminate"

/**
 * WHY an expectation could not be measured. Empty string when it was measured.
 * Render through `adminDashboard.hostedAgents.evals.detailCodes.<code>` — never
 * from the sibling `detail` field, which is English server prose.
 */
export type HostedAgentEvalDetailCode =
  | ""
  | "no_observation"
  | "no_tool_ledger"
  | "tool_ledger_truncated"
  | "tool_errored"
  | "duration_unavailable"
  | "unsupported_kind"

export const HOSTED_AGENT_EVAL_DETAIL_CODES: readonly Exclude<HostedAgentEvalDetailCode, "">[] = [
  "no_observation",
  "no_tool_ledger",
  "tool_ledger_truncated",
  "tool_errored",
  "duration_unavailable",
  "unsupported_kind",
] as const

export type HostedAgentEvalExpectationResult = {
  kind: HostedAgentEvalExpectationKind | string
  outcome: HostedAgentEvalOutcome
  /** Convenience mirror of `outcome === "passed"`; never trust it alone. */
  passed: boolean
  /** Short machine-near string, e.g. `status == completed`. Safe in font-mono. */
  expected: string
  /** Short machine-near string, e.g. `3 successful call(s)`. */
  observed: string
  detail_code: HostedAgentEvalDetailCode | string
  /** English server prose for logs. DO NOT RENDER. */
  detail?: string
}

/**
 * Case status. A real `failed` outranks an `indeterminate`; one indeterminate
 * expectation makes the whole case indeterminate (`case_outcome`, evals.py:489).
 * `errored` and `skipped` are further flavours of "not measured".
 */
export type HostedAgentEvalCaseStatus =
  | "passed"
  | "failed"
  | "indeterminate"
  | "errored"
  | "skipped"

/** Case-level reason code; own i18n map (`evals.caseDetailCodes.<code>`). */
export type HostedAgentEvalCaseDetailCode =
  | ""
  | "run_not_observed"
  | "dispatch_failed"
  | "suite_aborted_agent_paused"
  | "suite_aborted_cost_ceiling"

export const HOSTED_AGENT_EVAL_CASE_DETAIL_CODES: readonly Exclude<
  HostedAgentEvalCaseDetailCode,
  ""
>[] = [
  "run_not_observed",
  "dispatch_failed",
  "suite_aborted_agent_paused",
  "suite_aborted_cost_ceiling",
] as const

export type HostedAgentEvalUsage = {
  prompt_tokens: number
  completion_tokens: number
  cost_sek: number
}

export type HostedAgentEvalCaseResult = {
  case_id: string
  case_name: string
  /** The durable run behind this case; null when it was never dispatched. */
  run_id: string | null
  status: HostedAgentEvalCaseStatus
  /** The agent run's own terminal status, null when unobserved. */
  run_status: string | null
  expectations: HostedAgentEvalExpectationResult[]
  usage: HostedAgentEvalUsage
  duration_ms: number | null
  model: string | null
  timed_out: boolean
  /** False = no tool ledger, so tool expectations are indeterminate, not failed. */
  tool_calls_available: boolean
  tool_calls_truncated?: boolean
  detail_code: HostedAgentEvalCaseDetailCode | string
}

/** Suite status. `failed` means THE SUITE crashed, not that the agent failed. */
export type HostedAgentEvalRunStatus = "running" | "completed" | "failed"

/** Why the suite stopped early; remaining cases land as `skipped`. */
export type HostedAgentEvalAbortReason = "suite_aborted_agent_paused" | "suite_aborted_cost_ceiling"

/**
 * `score = passed / cases` — unmeasured cases sit in the denominator on
 * purpose, so the number never reads as a clean bill of health. Evidence
 * coverage `(passed + failed) / cases` is derived in the UI.
 */
export type HostedAgentEvalRunTotals = {
  cases: number
  passed: number
  failed: number
  indeterminate: number
  errored: number
  skipped: number
  score: number
}

/** Alias kept for call sites that name the totals without the `Run` infix. */
export type HostedAgentEvalTotals = HostedAgentEvalRunTotals

export type HostedAgentEvalModelProvenance = {
  selection: string | null
  model_id: string | null
}

/**
 * The krav 4 stamp (`build_provenance`, eval_runner.py:171). Taken before the
 * first turn and re-checked after the last.
 *
 * `verified: false` means the definition could not be re-read — that is "we
 * cannot say", NOT "verified comparable"; it must be surfaced separately from
 * `comparable` and must not be silenced by `comparable === true`.
 */
export type HostedAgentEvalProvenance = {
  agent_revision: number
  model: HostedAgentEvalModelProvenance
  tools: string[]
  tool_actions: Record<string, string[]>
  skills: string[]
  /** `{ skillName: sha256 | "unavailable" }`. */
  skill_digests: Record<string, string>
  libraries: string[]
  instructions_sha256: string
  min_hermes_version?: string | null
  hermes_agent_version?: string | null
  eval_case_ids: string[]
  /** Pins the exam itself: two runs with different hashes are different tests. */
  eval_cases_sha256: string
  verified: boolean
  /** False = the agent moved mid-suite; the run cannot support "v4 beats v3". */
  comparable: boolean
  agent_revision_at_finish: number | null
  instructions_sha256_at_finish: string | null
}

/** `public_run` (eval_runner.py:648) — the document minus Cosmos internals. */
export type HostedAgentEvalRun = {
  id: string
  tenant_id: string
  agent_id: string
  status: HostedAgentEvalRunStatus
  started_at: string
  finished_at: string | null
  started_by: string
  updated_at?: string | null
  /** True when a subset of cases was run — not comparable with a full suite. */
  partial: boolean
  aborted?: HostedAgentEvalAbortReason | string
  /** Written only on completion. */
  cost_sek?: number
  provenance: HostedAgentEvalProvenance
  /** Grows incrementally while the suite runs (`_persist_progress`). */
  case_results: HostedAgentEvalCaseResult[]
  totals: HostedAgentEvalRunTotals
}

export function isHostedAgentEvalRunActive(status: HostedAgentEvalRunStatus | string): boolean {
  return status === "running"
}

// -----------------------------------------------------------------------------
// Errors — a rejected suite has a REASON, and the admin gets to read it
// -----------------------------------------------------------------------------

/**
 * Stable codes the backend attaches. The last two are synthesised client-side
 * for the responses that carry no code (503 service missing, 404 unknown
 * agent), so every branch in the UI can key off one union.
 */
export type HostedAgentEvalErrorCode =
  | "invalid_eval_case"
  | "no_eval_cases"
  | "eval_case_not_found"
  | "eval_case_conflict"
  | "eval_run_not_found"
  | "agent_paused"
  | "selftest_in_flight"
  | "eval_suite_in_flight"
  | "eval_rate_limited"
  // Client-side synthetic (the backend sends these without a `code`):
  | "eval_service_unavailable"
  | "agent_not_found"

const SERVER_EVAL_ERROR_CODES: ReadonlySet<string> = new Set([
  "invalid_eval_case",
  "no_eval_cases",
  "eval_case_not_found",
  "eval_case_conflict",
  "eval_run_not_found",
  "agent_paused",
  "selftest_in_flight",
  "eval_suite_in_flight",
  "eval_rate_limited",
])

/**
 * A refusal with a machine code. Components map `code` to their own i18n key
 * — `message` is the backend's English text and is only a last-resort
 * fallback (it does carry the pydantic detail for `invalid_eval_case`).
 */
export class HostedAgentEvalError extends Error {
  readonly code: HostedAgentEvalErrorCode
  readonly status: number
  /** Only on 429 `eval_rate_limited`: seconds until the suite may run again. */
  readonly retryAfterSeconds: number | null

  constructor(init: {
    code: HostedAgentEvalErrorCode
    message: string
    status: number
    retryAfterSeconds?: number | null
  }) {
    super(init.message)
    this.name = "HostedAgentEvalError"
    this.code = init.code
    this.status = init.status
    this.retryAfterSeconds = init.retryAfterSeconds ?? null
  }
}

/** Narrow an unknown error to the typed eval refusal, or null. */
export function getHostedAgentEvalError(error: unknown): HostedAgentEvalError | null {
  return error instanceof HostedAgentEvalError ? error : null
}

/** True while a verification turn (selftest or another suite) holds the gate. */
export function isHostedAgentEvalInFlightCode(code: HostedAgentEvalErrorCode | string): boolean {
  return code === "selftest_in_flight" || code === "eval_suite_in_flight"
}

function toRetryAfterSeconds(value: unknown): number | null {
  const seconds = typeof value === "string" ? Number(value) : value
  return typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0
    ? Math.ceil(seconds)
    : null
}

function rethrowEvalError(error: unknown): never {
  if (!axios.isAxiosError(error) || !error.response) {
    throw error
  }
  const status = error.response.status
  const data = error.response.data as
    | { error?: string; code?: string; retry_after_seconds?: number }
    | undefined
  const message = data?.error || error.message
  const code = data?.code

  if (code && SERVER_EVAL_ERROR_CODES.has(code)) {
    throw new HostedAgentEvalError({
      code: code as HostedAgentEvalErrorCode,
      message,
      status,
      retryAfterSeconds:
        code === "eval_rate_limited" ? toRetryAfterSeconds(data.retry_after_seconds) : null,
    })
  }
  // Codeless responses that still deserve a specific message.
  if (status === 503) {
    throw new HostedAgentEvalError({ code: "eval_service_unavailable", message, status })
  }
  if (status === 404) {
    throw new HostedAgentEvalError({ code: "agent_not_found", message, status })
  }
  throw error
}

// -----------------------------------------------------------------------------
// Requests. `agentRef` is a slug or an id — the backend resolves both.
// -----------------------------------------------------------------------------

function evalsBase(agentRef: string): string {
  return `/admin/hosted-agents/${encodeURIComponent(agentRef)}`
}

/** Every eval case for one agent, enabled or not. */
export async function listHostedAgentEvalCases(agentRef: string): Promise<HostedAgentEvalCase[]> {
  try {
    const response = await api.get(`${evalsBase(agentRef)}/eval-cases`)
    const data = response.data || response
    return Array.isArray(data?.cases) ? (data.cases as HostedAgentEvalCase[]) : []
  } catch (error) {
    rethrowEvalError(error)
  }
}

/** Create one case (201). 409 `eval_case_conflict` at the 25-case cap. */
export async function createHostedAgentEvalCase(
  agentRef: string,
  input: HostedAgentEvalCaseInput
): Promise<HostedAgentEvalCase> {
  try {
    const response = await api.post(`${evalsBase(agentRef)}/eval-cases`, input)
    const data = response.data || response
    return data.case as HostedAgentEvalCase
  } catch (error) {
    rethrowEvalError(error)
  }
}

/** Replace one case. The service does an ETag-guarded read-modify-write. */
export async function updateHostedAgentEvalCase(
  agentRef: string,
  caseId: string,
  input: HostedAgentEvalCaseInput
): Promise<HostedAgentEvalCase> {
  try {
    const response = await api.put(
      `${evalsBase(agentRef)}/eval-cases/${encodeURIComponent(caseId)}`,
      input
    )
    const data = response.data || response
    return data.case as HostedAgentEvalCase
  } catch (error) {
    rethrowEvalError(error)
  }
}

/** Delete one case; resolves with the deleted case id. */
export async function deleteHostedAgentEvalCase(agentRef: string, caseId: string): Promise<string> {
  try {
    const response = await api.delete(
      `${evalsBase(agentRef)}/eval-cases/${encodeURIComponent(caseId)}`
    )
    const data = response.data || response
    return (data?.case_id as string) || caseId
  } catch (error) {
    rethrowEvalError(error)
  }
}

/** Recent runs, newest first. `limit` is clamped to 1..50 by the backend. */
export async function listHostedAgentEvalRuns(
  agentRef: string,
  limit = 20
): Promise<HostedAgentEvalRun[]> {
  try {
    const response = await api.get(`${evalsBase(agentRef)}/eval-runs?limit=${limit}`)
    const data = response.data || response
    return Array.isArray(data?.runs) ? (data.runs as HostedAgentEvalRun[]) : []
  } catch (error) {
    rethrowEvalError(error)
  }
}

/**
 * One run, including a suite still in progress — `case_results` grows as each
 * case lands, so a suite that dies halfway still reads as a partial answer
 * sheet. 404 `eval_run_not_found`.
 */
export async function getHostedAgentEvalRun(
  agentRef: string,
  runId: string
): Promise<HostedAgentEvalRun> {
  try {
    const response = await api.get(`${evalsBase(agentRef)}/eval-runs/${encodeURIComponent(runId)}`)
    const data = response.data || response
    return data.run as HostedAgentEvalRun
  } catch (error) {
    rethrowEvalError(error)
  }
}

/**
 * START the suite. Answers **202** with a `running` document — the turns
 * happen in a background task, so the caller must poll
 * {@link getHostedAgentEvalRun} until the status is terminal.
 *
 * Passing `caseIds` runs a subset and stamps `partial: true`, which is not
 * comparable with a full-suite run. Refusals: 409 `agent_paused`, 409
 * `selftest_in_flight` / `eval_suite_in_flight`, 429 `eval_rate_limited`
 * (with `retryAfterSeconds`), 400 `no_eval_cases`.
 */
export async function startHostedAgentEvalRun(
  agentRef: string,
  caseIds?: string[]
): Promise<HostedAgentEvalRun> {
  try {
    const response = await api.post(
      `${evalsBase(agentRef)}/eval-runs`,
      caseIds && caseIds.length > 0 ? { case_ids: caseIds } : {}
    )
    const data = response.data || response
    return data.run as HostedAgentEvalRun
  } catch (error) {
    rethrowEvalError(error)
  }
}
